import React, { useState } from 'react';
import InputMask from 'react-input-mask-next';
import { Plus, Trash2, Upload, CircleCheck as CheckCircle, CircleAlert as AlertCircle, Loader, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { RegistrationData, Contact } from './types';
import { emptyContact } from './types';

interface Props {
  data: RegistrationData;
  registrationId: string;
  slug: string;
  onComplete: (data: Partial<RegistrationData>) => void;
}

const MIN_CONTACTS = 4;

export default function AdditionalContactsStep({ data, registrationId, slug, onComplete }: Props) {
  const [contacts, setContacts] = useState<Contact[]>(
    data.contacts.length >= MIN_CONTACTS ? data.contacts : [
      ...data.contacts,
      ...Array(Math.max(0, MIN_CONTACTS - data.contacts.length)).fill(null).map(() => emptyContact())
    ]
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const clientPhone = data.phone;

  const updateContact = (index: number, field: keyof Contact, value: string) => {
    setContacts(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const addContact = () => {
    setContacts(prev => [...prev, emptyContact()]);
  };

  const removeContact = (index: number) => {
    if (contacts.length <= MIN_CONTACTS) return;
    setContacts(prev => prev.filter((_, i) => i !== index));
  };

  const uploadContactFile = async (index: number, field: 'photoFile' | 'conversationFile', file: File) => {
    const key = `${index}-${field}`;
    setUploading(prev => ({ ...prev, [key]: true }));
    try {
      const ext = file.name.split('.').pop();
      const category = field === 'photoFile' ? 'photo' : 'conversation';
      const filePath = `registrations/${slug}/contato-${index + 1}-${category}-${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('contract-files')
        .upload(filePath, file, { contentType: file.type, upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('contract-files').getPublicUrl(filePath);
      const url = urlData.publicUrl;

      const urlField = field === 'photoFile' ? 'photoUrl' : 'conversationUrl';
      setContacts(prev => prev.map((c, i) =>
        i === index ? { ...c, [field]: file, [urlField]: url } : c
      ));
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    const phones = new Set<string>();

    contacts.forEach((c, i) => {
      if (!c.nome.trim()) errs[`nome-${i}`] = 'Nome obrigatório';
      const digits = c.telefone.replace(/\D/g, '');
      if (digits.length < 10) errs[`tel-${i}`] = 'Telefone inválido';
      else if (phones.has(digits)) errs[`tel-${i}`] = 'Telefone duplicado';
      else if (digits === clientPhone) errs[`tel-${i}`] = 'Não pode ser o mesmo telefone do cliente';
      else phones.add(digits);
      if (!c.relacao.trim()) errs[`rel-${i}`] = 'Grau de relação obrigatório';
      if (!c.photoUrl) errs[`photo-${i}`] = 'Print da foto e número obrigatório';
      if (!c.conversationUrl) errs[`conv-${i}`] = 'Print da conversa obrigatório';
    });

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const relativePhones = contacts.map(c => ({
        numero: c.telefone,
        relacao: c.relacao,
        nome: c.nome,
        observacao: c.observacao,
      }));

      await supabase
        .from('client_self_registrations')
        .update({ relative_phones: relativePhones })
        .eq('id', registrationId);

      // Save contact file references to self_registration_files
      for (let i = 0; i < contacts.length; i++) {
        const c = contacts[i];
        if (c.photoUrl) {
          await supabase.from('self_registration_files').insert({
            registration_id: registrationId,
            file_name: `contato-${i + 1}-foto.jpg`,
            file_url: c.photoUrl,
            file_type: 'image',
            category: `contact_photo_${i + 1}`,
          });
        }
        if (c.conversationUrl) {
          await supabase.from('self_registration_files').insert({
            registration_id: registrationId,
            file_name: `contato-${i + 1}-conversa.jpg`,
            file_url: c.conversationUrl,
            file_type: 'image',
            category: `contact_conversation_${i + 1}`,
          });
        }
      }

      onComplete({ contacts });
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const FileUploadButton = ({
    index,
    field,
    label,
    url,
    accept,
  }: {
    index: number;
    field: 'photoFile' | 'conversationFile';
    label: string;
    url: string | null;
    accept: string;
  }) => {
    const key = `${index}-${field}`;
    const errKey = field === 'photoFile' ? `photo-${index}` : `conv-${index}`;
    const isUploading = uploading[key];
    return (
      <div>
        <label className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-colors ${url ? 'border-emerald-300 bg-emerald-50' : errors[errKey] ? 'border-red-300 bg-red-50' : 'border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100'}`}>
          {isUploading ? (
            <Loader className="w-4 h-4 text-gray-400 animate-spin flex-shrink-0" />
          ) : url ? (
            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          ) : (
            <Upload className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
          <span className={`text-xs font-medium ${url ? 'text-emerald-700' : 'text-gray-600'}`}>
            {isUploading ? 'Enviando...' : url ? 'Enviado!' : label}
          </span>
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadContactFile(index, field, f);
            }}
          />
        </label>
        {errors[errKey] && !url && <p className="text-red-500 text-xs mt-1">{errors[errKey]}</p>}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Contatos Adicionais</h2>
        <p className="text-gray-600 text-sm">
          Cadastre pelo menos <strong>{MIN_CONTACTS} contatos</strong> de pessoas que conhecem você.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <span className={`text-sm font-medium ${contacts.length >= MIN_CONTACTS ? 'text-emerald-600' : 'text-amber-600'}`}>
          {contacts.length} de {MIN_CONTACTS} contatos mínimos
        </span>
        <button
          onClick={addContact}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      </div>

      <div className="space-y-4">
        {contacts.map((contact, i) => (
          <div key={i} className={`bg-white border-2 rounded-xl overflow-hidden ${Object.keys(errors).some(k => k.endsWith(`-${i}`)) ? 'border-red-200' : 'border-gray-100'}`}>
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-semibold text-gray-700">Contato {i + 1}</span>
              </div>
              {contacts.length > MIN_CONTACTS && (
                <button onClick={() => removeContact(i)} className="text-red-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
                <input
                  type="text"
                  value={contact.nome}
                  onChange={(e) => updateContact(i, 'nome', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl text-base transition-colors ${errors[`nome-${i}`] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                  placeholder="Nome do contato"
                />
                {errors[`nome-${i}`] && <p className="text-red-500 text-xs mt-1">{errors[`nome-${i}`]}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone *</label>
                  <InputMask
                    mask="(99) 99999-9999"
                    value={contact.telefone}
                    onChange={(e) => updateContact(i, 'telefone', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl text-base transition-colors ${errors[`tel-${i}`] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    placeholder="(00) 00000-0000"
                  />
                  {errors[`tel-${i}`] && <p className="text-red-500 text-xs mt-1">{errors[`tel-${i}`]}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grau de relação *</label>
                  <input
                    type="text"
                    value={contact.relacao}
                    onChange={(e) => updateContact(i, 'relacao', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl text-base transition-colors ${errors[`rel-${i}`] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    placeholder="Mãe, Irmão..."
                  />
                  {errors[`rel-${i}`] && <p className="text-red-500 text-xs mt-1">{errors[`rel-${i}`]}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
                <input
                  type="text"
                  value={contact.observacao}
                  onChange={(e) => updateContact(i, 'observacao', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base"
                  placeholder="Informações adicionais sobre este contato"
                />
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Prints obrigatórios</p>
                <FileUploadButton
                  index={i}
                  field="photoFile"
                  label="Print da foto e número na agenda *"
                  url={contact.photoUrl}
                  accept="image/*"
                />
                <FileUploadButton
                  index={i}
                  field="conversationFile"
                  label="Print de conversa recente *"
                  url={contact.conversationUrl}
                  accept="image/*"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {contacts.length < MIN_CONTACTS && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          Adicione pelo menos {MIN_CONTACTS - contacts.length} contato(s) para continuar.
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={saving || contacts.length < MIN_CONTACTS}
        className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {saving ? <><Loader className="w-5 h-5 animate-spin" /> Salvando...</> : 'Continuar'}
      </button>
    </div>
  );
}
